import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle2,
  Copy,
  KeyRound,
  Loader2,
  Mail,
  Shield,
  ShieldAlert,
  UserCog,
  User,
  ShieldCheck,
  Calendar,
  Clock,
  Contact,
  Camera,
  Info
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { AuditService } from '@/services/AuditService';
import { useAuth } from '@/contexts/AuthContext';
import { AppUser } from '@/hooks/useUsuarios';
import {
  ADMIN_USER_ROLES,
  ADMIN_USER_STATUS_META,
  formatUserDate,
  getRoleLabel,
  getUserDisplayName,
  getUserInitials,
} from '@/lib/admin-users';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

type UserFormValues = {
  name: string;
  email: string;
  avatar_url: string;
  role: string;
  status: AppUser['status'];
};

interface UserEditAdminModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: AppUser | null;
  onSaved?: () => void;
}

export function UserEditAdminModal({
  open,
  onOpenChange,
  user,
  onSaved,
}: UserEditAdminModalProps) {
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [uploadPreview, setUploadPreview] = useState<string | null>(null);
  const [pendingAvatarFile, setPendingAvatarFile] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [activeTab, setActiveTab] = useState('perfil');

  const form = useForm<UserFormValues>({
    defaultValues: {
      name: '',
      email: '',
      avatar_url: '',
      role: 'HOMOLOGACAO',
      status: 'PENDING',
    },
  });

  useEffect(() => {
    if (!user) return;

    form.reset({
      name: getUserDisplayName(user),
      email: user.email || '',
      avatar_url: user.avatar_url || user.user_metadata?.avatar_url || '',
      role: user.role || 'HOMOLOGACAO',
      status: user.status,
    });
    setUploadPreview(null);
    setPendingAvatarFile(null);
    setActiveTab('perfil');
  }, [form, user]);

  const avatarSrc = useMemo(() => {
    if (uploadPreview) return uploadPreview;

    const current = form.watch('avatar_url') || user?.avatar_url || user?.user_metadata?.avatar_url;
    if (current) return current;

    return user?.email
      ? `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(user.email)}`
      : '/placeholder.svg';
  }, [form, uploadPreview, user]);

  const closeModal = () => {
    form.reset();
    setUploadPreview(null);
    setPendingAvatarFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    onOpenChange(false);
  };

  const uploadAvatarIfNeeded = async () => {
    if (!pendingAvatarFile || !user) {
      return form.getValues('avatar_url') || user?.avatar_url || user?.user_metadata?.avatar_url || null;
    }

    const ext = pendingAvatarFile.name.split('.').pop() || 'jpg';
    const path = `avatars/${user.id}/${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from('imgsinsecrj').upload(path, pendingAvatarFile, {
      upsert: false,
      contentType: pendingAvatarFile.type,
      cacheControl: '3600',
    });

    if (error) throw error;

    const { data } = supabase.storage.from('imgsinsecrj').getPublicUrl(path);
    return data.publicUrl;
  };

  const persistUserChanges = async (values: UserFormValues, statusOverride?: AppUser['status']) => {
    if (!user) return;

    setIsSaving(true);

    try {
      const avatarUrl = await uploadAvatarIfNeeded();
      const payload = {
        id: user.id,
        email: user.email,
        name: values.name.trim() || null,
        avatar_url: avatarUrl,
        role: values.role,
        status: statusOverride || values.status,
      };

      const { data: existingProfile, error: checkError } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', user.id)
        .maybeSingle();

      if (checkError) throw checkError;

      const query = existingProfile?.id
        ? supabase.from('profiles').update(payload).eq('id', user.id)
        : supabase.from('profiles').insert(payload);

      const { error: saveError } = await query;
      if (saveError) throw saveError;

      await AuditService.logActivity(
        'admin_update_user',
        {
          user_id: user.id,
          email: user.email,
          role: values.role,
          status: statusOverride || values.status,
        },
        currentUser?.id
      );

      await queryClient.invalidateQueries({ queryKey: ['usuarios'] });
      onSaved?.();

      toast({
        title: 'Usuário atualizado',
        description: 'Os dados do usuário foram salvos com sucesso.',
        variant: 'success',
      });

      setPendingAvatarFile(null);
      setUploadPreview(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Não foi possível salvar as alterações.';
      toast({
        title: 'Erro ao salvar usuário',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendResetPassword = async () => {
    if (!user?.email) return;

    setIsSendingReset(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
        redirectTo: `${window.location.origin}/#/reset-password-confirm`,
      });

      if (error) throw error;

      await AuditService.logActivity(
        'admin_send_password_reset',
        { user_id: user.id, email: user.email },
        currentUser?.id
      );

      toast({
        title: 'Redefinição enviada',
        description: 'O e-mail de redefinição de senha foi enviado para o usuário.',
        variant: 'success',
      });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Não foi possível enviar o e-mail de redefinição.';
      toast({
        title: 'Erro ao enviar redefinição',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsSendingReset(false);
    }
  };

  const handleSelectFile = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (loadEvent) => setUploadPreview((loadEvent.target?.result as string) || null);
    reader.readAsDataURL(file);
    setPendingAvatarFile(file);
  };

  const handleCopyUserId = async () => {
    if (!user?.id) return;

    await navigator.clipboard.writeText(user.id);
    toast({
      title: 'Identificador copiado',
      description: 'O ID do usuário foi copiado para a área de transferência.',
      variant: 'success',
    });
  };

  if (!user) return null;

  const displayName = form.watch('name')?.trim() || getUserDisplayName(user);
  const initials = getUserInitials(displayName);
  const statusMeta = ADMIN_USER_STATUS_META[form.watch('status')];

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && closeModal()}>
      <DialogContent className="flex flex-col gap-0 overflow-hidden p-0 max-w-4xl max-h-[92vh] rounded-3xl border border-border bg-card shadow-2xl">
        <DialogHeader className="border-b bg-muted/30 px-6 py-5 text-left border-border">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-500/10 rounded-2xl">
              <UserCog className="h-6 w-6 text-blue-500" />
            </div>
            <div>
              <DialogTitle className="text-xl font-bold text-foreground">Gestão do usuário</DialogTitle>
              <p className="mt-0.5 text-sm text-muted-foreground font-medium">
                Visualize e edite as informações, permissões e status de acesso do usuário.
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 bg-background">
          {/* Perfil Summary Banner */}
          <div className="px-8 py-8 bg-gradient-to-b from-muted/20 to-background border-b border-border">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
              <div className="relative group">
                <Avatar className="h-24 w-24 border-4 border-background shadow-2xl ring-1 ring-border">
                  <AvatarImage src={avatarSrc} alt={displayName} className="object-cover" />
                  <AvatarFallback className="bg-muted text-muted-foreground text-2xl font-bold">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <button
                  type="button"
                  onClick={handleSelectFile}
                  className="absolute bottom-0 right-0 p-2 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-all transform hover:scale-110 border-2 border-background z-20"
                >
                  <Camera className="h-4 w-4" />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </div>

              <div className="flex-1 space-y-2">
                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="text-3xl font-extrabold text-foreground tracking-tight">{displayName}</h3>
                  <Badge variant="outline" className={`h-7 px-3 rounded-full font-bold border-2 ${statusMeta.tone}`}>
                    {statusMeta.label}
                  </Badge>
                  <Badge variant="secondary" className="h-7 px-3 rounded-full font-bold bg-muted text-muted-foreground border border-border">
                    {getRoleLabel(form.watch('role'))}
                  </Badge>
                </div>
                
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                  <div className="flex items-center gap-2 text-muted-foreground text-sm font-medium">
                    <Mail className="h-4 w-4 opacity-50" />
                    {user.email}
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground text-sm font-medium">
                    <Calendar className="h-4 w-4 opacity-50" />
                    Criado em {formatUserDate(user.created_at, { dateStyle: 'medium' })}
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground text-sm font-medium">
                    <Clock className="h-4 w-4 opacity-50" />
                    Último acesso: {user.last_sign_in_at
                      ? formatUserDate(user.last_sign_in_at, { dateStyle: 'medium', timeStyle: 'short' })
                      : 'Nunca registrado'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="p-8">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
              <TabsList className="inline-flex h-12 items-center justify-start rounded-2xl bg-muted p-1.5 w-full md:w-auto">
                <TabsTrigger value="perfil" className="rounded-xl px-8 py-2 text-sm font-semibold transition-all data-[state=active]:bg-background data-[state=active]:text-blue-500 data-[state=active]:shadow-sm">
                  Perfil
                </TabsTrigger>
                <TabsTrigger value="acesso" className="rounded-xl px-8 py-2 text-sm font-semibold transition-all data-[state=active]:bg-background data-[state=active]:text-blue-500 data-[state=active]:shadow-sm">
                  Acesso
                </TabsTrigger>
                <TabsTrigger value="seguranca" className="rounded-xl px-8 py-2 text-sm font-semibold transition-all data-[state=active]:bg-background data-[state=active]:text-blue-500 data-[state=active]:shadow-sm">
                  Segurança
                </TabsTrigger>
              </TabsList>

              <Form {...form}>
                <form id="edit-admin-user-form" onSubmit={form.handleSubmit((values) => persistUserChanges(values))}>
                  <TabsContent value="perfil" className="mt-0 space-y-8 focus-visible:outline-none">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                      <div className="col-span-2 flex items-center gap-2 text-foreground font-bold text-lg mb-2">
                        <Contact className="h-5 w-5 text-blue-500" />
                        Dados cadastrais
                      </div>
                      
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem className="col-span-2 md:col-span-1">
                            <FormLabel className="text-muted-foreground font-bold ml-1">Nome completo</FormLabel>
                            <FormControl>
                              <div className="relative">
                                <User className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground opacity-50" />
                                <Input {...field} placeholder="Nome do usuário" className="h-12 pl-11 rounded-2xl border-input bg-background text-foreground focus:ring-blue-500/20 focus:border-blue-500 transition-all" />
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem className="col-span-2 md:col-span-1">
                            <FormLabel className="text-muted-foreground font-bold ml-1">E-mail institucional</FormLabel>
                            <FormControl>
                              <div className="relative">
                                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground opacity-50" />
                                <Input {...field} disabled className="h-12 pl-11 rounded-2xl bg-muted/50 text-muted-foreground border-input cursor-not-allowed opacity-70" />
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="role"
                        render={({ field }) => (
                          <FormItem className="col-span-2 md:col-span-1">
                            <FormLabel className="text-muted-foreground font-bold ml-1">Papel no sistema</FormLabel>
                            <div className="relative">
                              <ShieldCheck className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground opacity-50 z-10 pointer-events-none" />
                              <Select value={field.value} onValueChange={field.onChange}>
                                <FormControl>
                                  <SelectTrigger className="h-12 pl-11 rounded-2xl border-input bg-background text-foreground focus:ring-blue-500/20 focus:border-blue-500 transition-all">
                                    <SelectValue placeholder="Selecione um papel" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent className="rounded-2xl shadow-xl bg-card border-border">
                                  {ADMIN_USER_ROLES.map((role) => (
                                    <SelectItem key={role} value={role} className="rounded-xl my-0.5">
                                      {getRoleLabel(role)}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="status"
                        render={({ field }) => (
                          <FormItem className="col-span-2 md:col-span-1">
                            <FormLabel className="text-muted-foreground font-bold ml-1">Status operacional</FormLabel>
                            <div className="relative">
                              <Shield className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground opacity-50 z-10 pointer-events-none" />
                              <Select value={field.value} onValueChange={field.onChange}>
                                <FormControl>
                                  <SelectTrigger className="h-12 pl-11 rounded-2xl border-input bg-background text-foreground focus:ring-blue-500/20 focus:border-blue-500 transition-all">
                                    <SelectValue placeholder="Selecione um status" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent className="rounded-2xl shadow-xl bg-card border-border">
                                  {Object.entries(ADMIN_USER_STATUS_META).map(([status, meta]) => (
                                    <SelectItem key={status} value={status} className="rounded-xl my-0.5">
                                      {meta.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </TabsContent>

                  <TabsContent value="acesso" className="mt-0 space-y-8 focus-visible:outline-none">
                    <div className="flex items-center gap-2 text-foreground font-bold text-lg mb-6">
                      <ShieldCheck className="h-5 w-5 text-emerald-500" />
                      Controle de acesso
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      {Object.entries(ADMIN_USER_STATUS_META).map(([status, meta]) => {
                        const isActive = form.watch('status') === status;

                        return (
                          <button
                            key={status}
                            type="button"
                            onClick={() => form.setValue('status', status as AppUser['status'], { shouldDirty: true })}
                            className={`group relative rounded-3xl border-2 p-5 text-left transition-all duration-300 ${
                              isActive
                                ? 'border-emerald-500 bg-emerald-500/10 shadow-md ring-1 ring-emerald-500/20'
                                : 'border-border bg-card hover:border-muted-foreground/20 hover:shadow-lg'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-3">
                              <Badge variant="outline" className={`h-6 rounded-full font-bold ${meta.tone} border-2`}>
                                {meta.label}
                              </Badge>
                              {isActive ? (
                                <div className="bg-emerald-500 p-1 rounded-full">
                                  <CheckCircle2 className="h-3 w-3 text-white" />
                                </div>
                              ) : (
                                <div className="h-5 w-5 rounded-full border-2 border-border group-hover:border-muted-foreground/30" />
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground font-medium leading-relaxed">{meta.description}</p>
                          </button>
                        );
                      })}
                    </div>

                    <div className="mt-8 p-6 bg-muted/30 rounded-3xl border border-border flex items-start gap-4">
                      <div className="p-2 bg-blue-500/10 rounded-xl">
                        <Info className="h-5 w-5 text-blue-500" />
                      </div>
                      <div className="space-y-1">
                        <h4 className="text-sm font-bold text-foreground">Sobre as permissões</h4>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          O login só fica liberado quando o status estiver em <strong className="text-emerald-500">Aprovado</strong>. 
                          Contas pendentes, rejeitadas ou inativas permanecem bloqueadas na autenticação do sistema por segurança.
                        </p>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="seguranca" className="mt-0 space-y-8 focus-visible:outline-none">
                    <div className="flex items-center gap-2 text-foreground font-bold text-lg mb-6">
                      <KeyRound className="h-5 w-5 text-amber-500" />
                      Segurança da conta
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <Card className="rounded-3xl border-border bg-card shadow-sm hover:shadow-md transition-all">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base font-bold text-foreground flex items-center gap-2">
                            <Mail className="h-4 w-4 text-blue-500" />
                            Recuperação de Senha
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <p className="text-sm text-muted-foreground">
                            Envia um link seguro para o e-mail cadastrado permitindo que o usuário escolha uma nova senha.
                          </p>
                          <Button 
                            type="button" 
                            variant="outline" 
                            className="w-full h-12 rounded-2xl font-bold border-input bg-background hover:bg-muted" 
                            onClick={handleSendResetPassword} 
                            disabled={isSendingReset}
                          >
                            {isSendingReset ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              <Mail className="mr-2 h-4 w-4" />
                            )}
                            Enviar link de redefinição
                          </Button>
                        </CardContent>
                      </Card>

                      <Card className="rounded-3xl border-border bg-card shadow-sm hover:shadow-md transition-all">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base font-bold text-foreground flex items-center gap-2">
                            <Copy className="h-4 w-4 text-blue-500" />
                            Identificação Técnica
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <p className="text-sm text-muted-foreground">
                            Copie o identificador único (UUID) deste usuário para uso em suporte ou logs de auditoria.
                          </p>
                          <Button 
                            type="button" 
                            variant="outline" 
                            className="w-full h-12 rounded-2xl font-bold border-input bg-background hover:bg-muted" 
                            onClick={handleCopyUserId}
                          >
                            <Copy className="mr-2 h-4 w-4" />
                            Copiar ID do usuário
                          </Button>
                        </CardContent>
                      </Card>
                    </div>

                    <div className="p-6 bg-amber-500/5 rounded-3xl border border-amber-500/20 flex items-start gap-4 mt-6">
                      <div className="p-2 bg-amber-500/10 rounded-xl">
                        <ShieldAlert className="h-5 w-5 text-amber-500" />
                      </div>
                      <div className="space-y-1">
                        <h4 className="text-sm font-bold text-amber-500">Atenção</h4>
                        <p className="text-sm text-amber-500/70 leading-relaxed">
                          Alterações administrativas de 2FA e chaves de segurança serão restritas.
                          Mudanças sensíveis nestas configurações ficam registradas permanentemente no log de auditoria do sistema.
                        </p>
                      </div>
                    </div>
                  </TabsContent>
                </form>
              </Form>
            </Tabs>
          </div>
        </div>

        <div className="flex items-center justify-between border-t bg-muted/20 px-8 py-5 border-border">
          <div className="hidden sm:flex items-center gap-2 text-xs text-muted-foreground font-medium">
            <ShieldCheck className="h-3.5 w-3.5" />
            Alterações registradas via Auditoria
          </div>

          <div className="flex gap-3 w-full sm:w-auto">
            <Button 
              variant="outline" 
              onClick={closeModal} 
              disabled={isSaving || isSendingReset}
              className="flex-1 sm:flex-none h-11 px-6 rounded-xl font-bold border-input"
            >
              Cancelar
            </Button>
            <Button 
              type="submit" 
              form="edit-admin-user-form" 
              variant="success" 
              disabled={isSaving || isSendingReset}
              className="flex-1 sm:flex-none h-11 px-10 rounded-xl font-bold shadow-lg shadow-emerald-500/10"
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Salvar alterações
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default UserEditAdminModal;
